import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'dashboard/composables/store';

export function useKanbanBoards() {
  const store = useStore();
  const myTeams = computed(() => store.getters['teams/getMyTeams'] || []);
  const allBoards = computed(() => store.getters['boards/getBoards'] || []);
  const board = computed(() => store.getters['boards/getBoard']);
  const boardsLoading = computed(
    () => store.getters['boards/getUIFlags'].isFetching
  );

  // Filtrar boards dos times do usuário
  const availableBoards = computed(() => {
    if (!myTeams.value.length || !allBoards.value.length) return [];
    const teamIds = myTeams.value.map(team => team.id);
    return allBoards.value.filter(board => teamIds.includes(board.team_id));
  });

  // Verificar se precisa mostrar o select (múltiplos boards)
  const showBoardSelector = computed(() => availableBoards.value.length > 1);

  // Board selecionado
  const selectedBoardId = ref(null);
  const hasTriedLoadingFirstBoard = ref(false);

  const loadBoard = async boardId => {
    if (!boardId) return;
    const boardToLoad = availableBoards.value.find(b => b.id === boardId);
    if (boardToLoad) {
      await store.dispatch('boards/getByTeam', {
        teamId: boardToLoad.team_id,
        boardId: boardToLoad.id,
      });
    }
  };

  const onBoardChange = async boardId => {
    selectedBoardId.value = boardId;
    await loadBoard(boardId);
  };

  watch(
    () => board.value?.id,
    newBoardId => {
      if (!newBoardId) return;
      const boardExists = availableBoards.value.some(b => b.id === newBoardId);
      if (!boardExists) return;
      if (selectedBoardId.value !== newBoardId) {
        selectedBoardId.value = newBoardId;
      }
    }
  );

  // Watch for availableBoards to load first board when teams are loaded
  watch(
    () => availableBoards.value,
    (boards) => {
      // Only auto-load first board if we haven't tried yet and there are boards available
      if (!hasTriedLoadingFirstBoard.value && boards.length > 0 && !selectedBoardId.value) {
        hasTriedLoadingFirstBoard.value = true;
        const firstBoard = boards[0];
        selectedBoardId.value = firstBoard.id;
        loadBoard(firstBoard.id);
      }
    },
    { immediate: true }
  );

  const loadBoards = async () => {
    try {
      await store.dispatch('boards/getAll');
      await nextTick();

      // Try to load first board if available (teams may not be loaded yet)
      if (availableBoards.value.length > 0 && !hasTriedLoadingFirstBoard.value) {
        hasTriedLoadingFirstBoard.value = true;
        const firstBoard = availableBoards.value[0];
        selectedBoardId.value = firstBoard.id;
        await loadBoard(firstBoard.id);
      }
    } catch (error) {
      console.error('Erro ao carregar boards:', error);
    }
  };

  const truncateTeamName = name => {
    return name.length > 25 ? `${name.slice(0, 25)}...` : name;
  };

  const getTeamName = teamId => {
    const team = myTeams.value.find(t => t.id === teamId);
    const name = team ? team.name : `Time #${teamId}`;
    return truncateTeamName(name);
  };

  return {
    board,
    availableBoards,
    showBoardSelector,
    selectedBoardId,
    boardsLoading,
    loadBoard,
    onBoardChange,
    loadBoards,
    getTeamName,
  };
}

